import hubApiClient from 'hub-http/clients/apiClient';
import { gate } from 'hub-http/gates';
import userInfo from 'hub-http/userInfo';
import Raven from 'raven-js';
import { useEffect } from 'react';
import { getIsActiveInSessionTimeWindow } from './activityTracker';
import { debugLog, getDebugMode } from './fullstory-debug';
import { FullstoryAPI } from './fullstoryAPI';
async function fetchSensitiveDataStatus() {
  try {
    const response = await hubApiClient.get('data-privacy-settings/hub-settings');
    if (!response || typeof response !== 'object' || Array.isArray(response) || !('sensitiveDataEnabled' in response)) {
      return {
        isSensitiveDataEnabledForPortal: true
      };
    }
    return {
      isSensitiveDataEnabledForPortal: Boolean(response['sensitiveDataEnabled'])
    };
  } catch (e) {
    // default to being safe/restricting capture if we can't get the status
    return {
      isSensitiveDataEnabledForPortal: true
    };
  }
}
function isSeleniumSession() {
  return document.cookie.includes('hs_selenium');
}
/** Reminder: When creating new gates, mark them as protected to avoid stale deletion */
const AppGates = {
  DatasetBuilder: 'DOFS:DatasetBuilder',
  DatasetManager: 'DOFS:DatasetManager',
  DataQualityCommandCenter: 'DOFS:DataQualityCommandCenter',
  ListSegments: 'DOFS:ListSegments',
  CustomBehavioralEvents: 'DOFS:CustomBehavioralEvents',
  AnalyticsToolsPopup: 'DOFS:AnalyticsToolsPopup',
  JourneyAnalytics: 'DOFS:JourneyAnalytics',
  JourneyAutomation: 'DOFS:JourneyAutomation',
  AttributionBuilder: 'DOFS:AttributionBuilder',
  CustomReportBuilder: 'DOFS:CustomReportBuilder',
  SingleObjectReportBuilder: 'DOFS:SingleObjectReportBuilder',
  ReportsList: 'DOFS:ReportsList',
  ReportingTemplateLibrary: 'DOFS:ReportingTemplateLibrary',
  AutomationCanvas: 'DOFS:AutomationCanvas',
  AutomationIndex: 'DOFS:AutomationIndex',
  DataSync: 'DOFS:DataSync',
  ChatSpot: 'DOFS:ChatSpot',
  CRMImportUI: 'DOFS:CRMImportUI',
  Forecasting: 'DOFS:Forecasting',
  ForecastingSettings: 'DOFS:ForecastingSettings',
  HelpDesk: 'DOFS:HelpDesk',
  HelpDeskSettings: 'DOFS:HelpDeskSettings',
  CustomerHealthScoreBuilder: 'DOFS:CustomerHealthScoreBuilder',
  ReportingGoals: 'DOFS:ReportingGoals',
  QuotesAdminUI: 'DOFS:QuotesAdminUI',
  AutomationOverview: gate('DOFS:AutomationOverview'),
  DataSharingUI: gate('DOFS:DataSharingUI'),
  DataModelOverview: gate('DOFS:DataModelOverview')
};
const DataStudioAppKeys = ['DatasetManager', 'DatasetBuilder', 'DataSync', 'CRMImportUI', 'AutomationCanvas', 'AutomationIndex'];
export async function initializeFullstory(config) {
  try {
    var _config$useFreeSessio;
    debugLog('Initializing Fullstory');
    if (isSeleniumSession()) {
      debugLog('Selenium session detected.  Aborting initialization.');
      return Promise.resolve({
        initialized: false,
        reason: 'SELENIUM_SESSION_DETECTED'
      });
    }
    if (FullstoryAPI.isInitialized()) {
      debugLog('Fullstory script already present. Aborting initialization.');
      return Promise.resolve({
        initialized: true
      });
    }
    const [userData, {
      isSensitiveDataEnabledForPortal
    }] = await Promise.all([userInfo(), fetchSensitiveDataStatus()]);
    if (isSensitiveDataEnabledForPortal) {
      Raven.captureMessage('Sensitive data exclusion enabled for portal.', {
        extra: {
          type: 'fullstory_sensitive_data_exclusion',
          portalId: userData.portal.portal_id,
          userId: userData.user.user_id,
          email: userData.user.email,
          appKey: config.appKey
        },
        // Events of this level will be stored in the `ERRORS.SENTRY_V3` custom LogFetch event namespace
        level: 'info'
      });
      return Promise.resolve({
        initialized: false,
        reason: 'SENSITIVE_DATA_EXCLUSION'
      });
    }
    if (!AppGates[config.appKey]) {
      // log without too much FS info so folks can debug why things aren't working when they first are configuring FS for their app
      console.log('App key not found in DOFS configuration.');
      return {
        initialized: false,
        reason: 'BAD_APP_CONFIGURATION'
      };
    }
    const {
      user: {
        user_id,
        email
      },
      gates
    } = userData;
    const isHubspotter = email.includes('@hubspot.com');
    const isDataStudioBetaRecording = DataStudioAppKeys.includes(config.appKey) && gates.includes(gate('DataStudio:BetaAccess')); // This gate check is temporary until data studio is fully launched

    const isUngatedForApp = config.UNSAFE_IGNORE_GATE || config.ignoreGate ? true : gates.includes(AppGates[config.appKey]) || isDataStudioBetaRecording;
    const beginFullstory = isChainedSession => {
      // If script fails to load, the error will be reported through _fs_on_error
      // with all the context we need for debugging
      FullstoryAPI.initialize({
        portalId: userData.portal.portal_id,
        userId: userData.user.user_id,
        email: userData.user.email,
        appKey: config.appKey,
        isHubspotter,
        isChainedSession,
        isUngatedForApp,
        isSensitiveDataEnabled: isSensitiveDataEnabledForPortal,
        isDataStudioBetaRecording
      });
      if (config.testMode) {
        FullstoryAPI.shutdown();
        // used to denote that the fullstory recording should be discarded
        window._fs_dev_mode = true;
        debugLog('Fullstory test mode enabled, aborting.');
        // fake on ready complete for the test flow
        return Promise.resolve({
          initialized: true
        });
      }
      return new Promise(resolve => {
        FullstoryAPI.onReady(({
          sessionUrl
        }) => {
          FullstoryAPI.identify(user_id, Object.assign({
            appKey: config.appKey,
            portalId: userData.portal.portal_id
          }, config.identityVariables, {
            isHubspotter,
            isChainedSession,
            // @ts-expect-error document.prerendering is experimental & only available in Chrome
            isPrerendering: !!document.prerendering,
            isDataStudioBetaRecording
          }));
          Raven.setExtraContext({
            fullstory: {
              fullstorySessionLink: sessionUrl
            }
          });

          // Critical block, don't mess with this
          Raven.setDataCallback((data, originalCallback) => {
            try {
              //  unsafe access, only attempt in try catch
              FullstoryAPI.log('error', data.exception.values[0].value);
            } catch (e) {
              debugLog('Fullstory sentry spying failed', e);
            }
            return originalCallback ? originalCallback(data) : data;
          });
          resolve({
            initialized: true
          });
        });
      });
    };
    if (getDebugMode()) {
      return beginFullstory(false);
    }
    if (isHubspotter && !config.allowHubspotterTracking) {
      debugLog('User is a Hubspotter and Hubspotter tracking is not allowed. Aborting.');
      return {
        initialized: false,
        reason: 'HUBSPOTTER_NOT_ALLOWED'
      };
    }
    if (isUngatedForApp) {
      return beginFullstory(false);
    }
    if ((_config$useFreeSessio = config.useFreeSessionChaining) !== null && _config$useFreeSessio !== void 0 ? _config$useFreeSessio : true) {
      debugLog('User is not ungated for this app. Checking recent sessions...');
      if (getIsActiveInSessionTimeWindow()) {
        debugLog('Recent session found, chaining sessions.');
        return beginFullstory(true);
      } else {
        debugLog('No recent session found, initialization aborted.');
        return {
          initialized: false,
          reason: 'SAMPLE_FILTERED'
        };
      }
    }
    return {
      initialized: false,
      reason: 'SAMPLE_FILTERED'
    };
  } catch (error) {
    debugLog('Error with Fullstory initialization, aborting.', error);
    Raven.captureException(error instanceof Error ? error : new Error('Unknown initialization error'));
    return {
      initialized: false,
      reason: 'UNKNOWN'
    };
  }
}

/**
 * Initializes Fullstory Tracking
 * Note that this component is not reactive, it will only be
 * called once per mount.
 * If you need the script to update or initialize after a userID is available,
 * please put the script lower in your Component tree.
 */
export function useFullstory(config) {
  useEffect(() => {
    if (config.ignoreUser) {
      return;
    }
    initializeFullstory(config).catch(e => {
      Raven.captureException(e);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}